import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getChatSessions,
  getChatSession,
  getChatMessages,
  createChatSession,
  sendMessage,
  endChatSession,
  markMessagesAsRead,
  type CreateChatSessionDto,
  type SendMessageDto
} from '@/services/chat'
import { ChatSession } from '@/types'

// Query keys
export const chatKeys = {
  all: ['chat'] as const,
  sessions: () => [...chatKeys.all, 'sessions'] as const,
  session: (sessionId: string) => [...chatKeys.sessions(), sessionId] as const,
  messages: (sessionId: string) => [...chatKeys.all, 'messages', sessionId] as const,
}

// Get all chat sessions
export const useChatSessions = (params?: {
  status?: 'active' | 'completed' | 'cancelled'
  page?: number
  limit?: number
}) => {
  return useQuery({
    queryKey: chatKeys.sessions(),
    queryFn: () => getChatSessions(params),
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

// Get specific chat session with messages
export const useChatSession = (sessionId?: string) => {
  return useQuery({
    queryKey: chatKeys.session(sessionId || ''),
    queryFn: () => getChatSession(sessionId!),
    enabled: !!sessionId,
    staleTime: 30 * 1000, // 30 seconds
  })
}

// Get messages for a chat session
export const useChatMessages = (
  sessionId?: string,
  params?: {
    page?: number
    limit?: number
    lastMessageId?: string
  }
) => {
  return useQuery({
    queryKey: chatKeys.messages(sessionId || ''),
    queryFn: () => getChatMessages(sessionId!, params),
    enabled: !!sessionId,
    staleTime: 10 * 1000, // 10 seconds for real-time feel
  })
}

// Create a new chat session
export const useCreateChatSession = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateChatSessionDto) => createChatSession(data),
    onSuccess: (newSession) => {
      // Add the new session to the sessions list cache
      queryClient.setQueryData(chatKeys.sessions(), (oldData: any) => {
        if (!oldData) return { sessions: [newSession], total: 1 }
        return {
          sessions: [newSession, ...oldData.sessions],
          total: oldData.total + 1
        }
      })
      
      // Set the new session details in cache
      queryClient.setQueryData(chatKeys.session(newSession.id), newSession)
    },
  })
}

// Send a message
export const useSendMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SendMessageDto) => sendMessage(data),
    onSuccess: (newMessage, variables) => {
      // Add the new message to the messages cache
      queryClient.setQueryData(chatKeys.messages(variables.sessionId), (oldData: any) => {
        if (!oldData) return { messages: [newMessage], hasMore: false }
        return {
          ...oldData,
          messages: [...oldData.messages, newMessage]
        }
      })
      
      // Update the session cache to reflect latest message
      queryClient.invalidateQueries({ queryKey: chatKeys.session(variables.sessionId) })
    },
  })
}

// End a chat session
export const useEndChatSession = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId }: { sessionId: string }) => 
      endChatSession(sessionId, {}),
    onSuccess: (updatedSession, variables) => {
      // Update the session details cache
      queryClient.setQueryData(chatKeys.session(variables.sessionId), updatedSession)
      
      // Update the session in the sessions list cache
      queryClient.setQueryData(chatKeys.sessions(), (oldData: any) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          sessions: oldData.sessions.map((session: ChatSession) =>
            session.id === variables.sessionId ? updatedSession : session
          )
        }
      })
      
      // Invalidate related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: chatKeys.sessions() })
    },
  })
}

// Mark messages as read
export const useMarkMessagesAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (sessionId: string) => markMessagesAsRead(sessionId),
    onSuccess: (_, sessionId) => {
      // Invalidate the session to update unread count
      queryClient.invalidateQueries({ queryKey: chatKeys.session(sessionId) })
      queryClient.invalidateQueries({ queryKey: chatKeys.sessions() })
    },
  })
}